import { useEffect, useMemo, useState } from 'react';

import { sfetchJson } from 'Internals/sfetch';

import { EntityTypeKind } from '../api/pipeTypes.api';

import type { Pipe, PipeConfig } from 'Types/pipes.types';

type UsePipeEntitiesProps = {
  pipeId: string;
  pipeConfig: PipeConfig;
  limit: number;
  sinkDatasetId: string;
  stage: EntityTypeKind;
  subUrl: string;
  token: string;
};

function usePipeEntities(props: UsePipeEntitiesProps) {
  const { pipeId, pipeConfig, limit, sinkDatasetId, stage, subUrl, token } = props;

  const [entities, setEntities] = useState([]);
  const [selectedEntityIndex, setSelectedEntityIndex] = useState(0);

  const selectNextEntity = () => {
    if (entities.length - 1 > selectedEntityIndex) {
      setSelectedEntityIndex((idx) => idx + 1);
    }
  };

  const selectPreviousEntity = () => {
    if (selectedEntityIndex > 0) {
      setSelectedEntityIndex((idx) => idx - 1);
    }
  };

  useEffect(() => {
    const requestOptions: RequestInit = {
      credentials: 'include',
      headers: { Authorization: `bearer ${token}` },
    };

    if (!pipeConfig) {
      return;
    }

    const datasetSinkUrl = `${subUrl}/datasets/${encodeURIComponent(
      sinkDatasetId
    )}/entities?deleted=false&history=false&reverse=true&uncommitted=false&limit=${limit}`;

    const stageUrl = `${subUrl}/pipes/${encodeURIComponent(
      pipeId
    )}/entities?stage=${stage}&limit=${limit}`;

    // try if there are any entities in the sink dataset
    if (stage === 'sink' && sinkDatasetId) {
      sfetchJson(datasetSinkUrl, requestOptions)
        .then((entities) => {
          if (entities.length < 1) {
            throw new Error();
          } else {
            setEntities(entities);
          }
        })
        .catch(() => {
          sfetchJson(stageUrl, requestOptions)
            .then((entities) => setEntities(entities))
            .catch(() => {
              setEntities([]);
            });
        });
    } else {
      sfetchJson(stageUrl, requestOptions)
        .then((entities) => setEntities(entities))
        .catch(() => {
          setEntities([]);
        });
    }
  }, [limit, pipeConfig, stage, subUrl, token]);

  const selectedEntity = useMemo(() => {
    return entities[selectedEntityIndex];
  }, [entities, selectedEntityIndex]);

  return {
    entities,
    selectedEntity: selectedEntity,
    selectedEntityIndex,
    selectNextEntity,
    selectPreviousEntity,
  };
}

export default usePipeEntities;
